import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { PurchLineItem } from '@app/views/components/purch-head/purch-head-purch-lines/purch-head-purch-lines.component';
import { BehaviorSubject, debounceTime, from, Subject, switchMap } from 'rxjs';
@Injectable({
    providedIn: 'root',
})
export class PurchHeadService {
    private currentPurchHeadSubject = new BehaviorSubject<PurchHeadGet | null>(null);

    public currentPurchHead$ = this.currentPurchHeadSubject.asObservable();

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
    ) {
        this.getPurchHeadSubject
            .pipe(
                debounceTime(100),
                switchMap((params) => from(this.getPurchHead(params))),
            )
            .subscribe({ next: (value) => this.currentPurchHeadSubject.next(value) });
    }

    private getPurchHeadSubject = new Subject<PurchHeadGetParams>();

    public setPurchHeadFromParams(params: PurchHeadGetParams | null) {
        if (!!params) {
            this.getPurchHeadSubject.next(params);
        } else {
            this.currentPurchHeadSubject.next(null);
        }
    }

    public getCurrentPurchHead(): PurchHeadGet | null {
        return this.currentPurchHeadSubject.getValue();
    }

    /**
     * Retrieves the purchhead get fro the given internal no.
     *
     * @datatask 686
     * @datataskName sp_ws_p2_purchhead_get
     * @param params the internal of the purchhead to retrieve.
     * @returns promise containing the purchhead get for the given internal no.
     */
    public async getPurchHead(params: PurchHeadGetParams): Promise<PurchHeadGet> {
        return (await this.datatask.Post(686, params))[0];
    }

    /**
     *Retrieves purchheads get for the given argtype and value.

     * @datatask 681
     * @datataskName sp_ws_p2_w_purchheads_setting
     * @param params params for the purchheads get.
     * @returns promise containing the page get for purch-heads page
     */
    public async getPurchHeads(params: PurchHeadsGetParams): Promise<PurchHeadsGet> {
        return (await this.datatask.Post(681, params))[0];
    }

    public async getDeleteConfirmationMessage(params: PurchHeadGetParams) {
        return (await this.datatask.Post(3720, params))?.[0]?.confirmation_message;
    }

    /**
     * Deletes the purchhead with the given purch internal no.
     *
     * @datatask 735
     * @datataskName sp_ws_p2_purchhead_delete
     * @param params deletes the purch head with the given purch internal no.
     * @returns promise.
     */
    public async deletePurchHead(params: PurchHeadGetParams): Promise<void> {
        try {
            await this.datatask.Post(735, params);
            this.currentPurchHeadSubject.next(null);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /**
     * Retrieves person info for the given pers_no.
     *
     * @datatask 700
     * @datataskName sp_ws_p2_person_info_get
     * @param params the pers no to find person info for.
     * @returns returns a promise containing person info for the given pers no.
     */
    public async getPersonInfo(params: PersonInfoParams): Promise<PersonInfo> {
        return (await this.datatask.Post(700, params))[0] ?? { pers_email: '', pers_telephones: '' };
    }

    /**
     * Retrieves the department which has the given department id.
     *
     * @datatask 2228
     * @datataskName sp_ws_p2_department_get
     * @params the deparmtent id to retrieve department for.
     * @returns a promise containing the department.
     */
    public async getDepartment(params: GetDepartmentParams): Promise<DepartmentGet> {
        return (await this.datatask.Post(2228, params))[0];
    }

    /**
     * Retrieves list of freight rate sets for the given cust no.
     *
     * @datatask 1551
     * @datataskName sp_ws_p2_freightrateset_select_list
     * @param params the cust no to retrieve freight rate sets for.
     * @returns a promise containing list of freight rate sets.
     */
    public async getFreightRateSets(params: FreightRateSetsParams): Promise<FreightRateSetItem[]> {
        return this.datatask.Post(1551, params);
    }

    /**
     * Retrieves list of freight rates for the given freight rate set keyno.
     *
     * @datatask 1552
     * @datataskName sp_ws_p2_freightrate_select_list
     * @param params the freight rate sets keyno to retrieve freight rate for.
     * @returns a promise containing list of freight rates.
     */
    public async getFreightRates(params: FreightRatesParams): Promise<FreightRateItem[]> {
        return this.datatask.Post(1552, params);
    }

    /**
     * Retrieves the freight rate for the given keyno.
     *
     * @datatask 1548
     * @datataskName sp_ws_p2_freightrate_get
     * @param params
     * @returns a promise containing the freight rate get.
     */
    public async getFreightRate(params: GetFreightRateParams): Promise<FreightRateGet> {
        return (await this.datatask.Post(1548, params))[0];
    }

    /**
     * Updates the purchlines with the freight set on purch-head.
     *
     * @datatask 1555
     * @datataskName sp_ws_p2_purchhead_update_freight
     * @param params purch internal no to update price for.
     * @returns errocode and errormessage.
     */
    public async updateFreight(params: UpdateFreightParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1555, params))[0];
    }
    /**
     * Cancel the rest of the purchase.
     *
     * @datatask 1989
     * @datataskName sp_ws_p2_purchhead_cancel_rest
     * @param params purch internal no to cancel the rest for.
     * @returns errocode and errormessage.
     */
    public async cancelRest(params: CancelRestParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1989, params))[0];
    }

    /**
     * Retrieves list of purchase statuses.
     *
     * @datatask 682
     * @datataskName sp_ws_p2_purchasestatus_list
     * @param params configruation of the ourchase statuses list to retrieve.
     * @returns promise containing list of purchase statuses.
     */
    public async getPurchaseStatuses(params?: Partial<PurchaseStatusesParams>): Promise<PurchaseStatusItem[]> {
        return this.datatask.Post(682, params);
    }

    /**
     * Saves the given purchhead with the changes in contains.
     *
     * @datatask 690
     * @datataskName sp_ws_p2_purchhead_save
     * @param params the purchhead to save.
     * @returns promise containing errorcode and errormessage from the save response.
     */
    public async savePurchHead(params: PurchHeadGet, showErrorModal = true): Promise<ErrorMessageResponse> {
        try {
            const { errorcode, errormessage }: ErrorMessageResponse = (await this.datatask.Post(690, params))[0];

            if ('' + errorcode !== '0') {
                if (showErrorModal) {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
                }

                throw { errorcode, errormessage };
            } else {
                return { errorcode, errormessage };
            }
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /**
     * Retrieves list of possible iniated by labourers.
     *
     * @datatask 1045
     * @datataskName sp_ws_p2_portal_user_list_labours
     * @returns promise containing list of possible iniated by people.
     */
    public async getIniatedByList(): Promise<Item[]> {
        return this.datatask.Post(1045);
    }

    /**
     * Converts the purchhead with the given purch internal no to purchorder.
     *
     * @datatask 702
     * @datataskName sp_ws_p2_purchhead_convert_to_purchorder
     * @param params the purch internal no of the purchhead to convert to purchorder.
     * @returns promise which resolves when the convertion is complete.
     */
    public async convertToPurchOrder(params: PurchHeadGetParams): Promise<void> {
        try {
            await this.datatask.Post(702, params);
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            this.setPurchHeadFromParams(params);
        }
    }

    /**
     * Converts the order with the given internal no to a sales order.
     *
     * @datatask 1599
     * @datataskName sp_ws_p2_w_purchhead_purchorder_to_salesorder
     * @param params the internal no of the purch order to convert to sales order.
     * @returns promise contanining errorcode and message.
     */
    public async convertPurchOrderToSalesOrder(params: ConvertPurchOrderToSalesOrderParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(1599, params))[0];
    }

    /**
     * Retrieves progress information about the convertion to sales order.
     *
     * @datatask 1980
     * @datataskName sp_ws_p2_w_purchhead_progress_to_salesorder
     * @param params the purch internal no of the purchase order to get progress for.
     * @returns
     */
    public async getConvertToSalesOrderProgress(params: ConvertToSalesOrderProgressParams): Promise<ConvertToSalesOrderProgress> {
        return (await this.datatask.Post(1980, params))[0];
    }

    /**
     * Retrieves list of purchheads matching the given params.
     *
     * @datatask 683
     * @datataskName sp_ws_p2_purchhead_list
     * @param params the filters of the purchhead list.
     * @returns promise containing list of purchheads matching the given filters.
     */
    public getPurchHeadsList(params: PurchHeadsGet): Promise<Item[]> {
        return this.datatask.Post(683, params);
    }

    /**
     * Retrieves the object used for creating a new purch head.
     *
     * @datatask 687
     * @datataskName sp_ws_p2_w_purchheadcreate_get
     * @param params the context for creating new purch head.
     * @returns promise containing the purch head create object.
     */
    public async getPurchHeadCreate(params: PurchHeadsGetParams): Promise<PurchHeadCreateGet> {
        return (await this.datatask.Post(687, params))[0];
    }

    /**
     * Saves the given ourch head create object as a new purch head.
     *
     * @datatask 688
     * @datataskName sp_ws_p2_w_purchheadcreate_save
     * @param params saves the given purch head create object as a new purch head.
     * @returns promise containing potential error message and context for futher navigating state.
     */
    public async savePurchHeadCreate(params: PurchHeadCreateGet): Promise<SavePurchHeadCreateResponse> {
        return (await this.datatask.Post(688, params))[0];
    }
    /**
     * Retrieves the page get for the purchhead receive simplified.
     *
     * @datatask 3976
     * @datataskName tt_purchhead_receive_simplified_get
     * @param params the purch internal no for the purchhead to receive simplified for.
     * @returns promise containing page get.
     */
    public async getPurchHeadReceiveSimplified(params: GetPurchHeadReceiveSimplifiedParams): Promise<PurchHeadReceiveSimplifiedGet> {
        return (await this.datatask.Post(3976, params))[0];
    }

    /**
     * Creates purch head received.
     *
     * @datatask 3977
     * @datataskName tt_purchhead_receive_simplified_create
     * @param params for creating received purch head.
     * @returns promise containing errormessage ang code.
     */
    public async createPurchHeadReceiveSimplified(params: CreatePurchHeadReceiveSimplifiedParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3977, params))[0];
    }
}

export interface CreatePurchHeadReceiveSimplifiedParams extends PurchHeadReceiveSimplifiedGet {
    records: PurchLineItem[];
}

export interface PurchHeadCreateGet {
    order_date: string;
    purchasestatus_id: string;
    supplier_no: string | number;
    searchtext_supplier: string;
    supplier_address: string;
    label: string;
    businessco_no: string | number;
    stockplace_id: string;
    project_keyno: string | number;
    project_name: string;
    show_project: '1' | '0';
    department_id: string;
    show_department_id: '1' | '0';
    show_setting: '1' | '0';
    setting_department_is_required: '1' | '0';
}

export interface SavePurchHeadCreateResponse extends ErrorMessageResponse {
    purch_internal_no: string;
    webpagename: string;
    parms: string;
}

export interface PurchaseStatusItem {
    purchasestatus_id: string;
    purchasestatus_name: string;
    orderby: string;
}

export interface PurchaseStatusesParams {
    addall: '1' | '0';
    createnew: '1' | '0';
    add_include_active: '1' | '0';
}

export interface CancelRestParams {
    purch_internal_no: string | number;
}

export interface UpdateFreightParams {
    purch_internal_no: string | number;
}

export interface GetFreightRateParams {
    freightrate_keyno: string | number;
}

export interface FreightRateGet {
    freightrate_keyno: string | number;
    from_supplier_name: string;
    to_stockplace_name: string;
    freightratetype_name: string;
    freight_main_rate_locval: string | number;
    freight_last_rate_locval: string | number;
    customs_last_rate_locval: string | number;
    volume_m3: string | number;
    isdefault: '1' | '0';
}

export interface FreightRateSetsParams {
    cust_no: string | number;
}

export interface FreightRateSetItem {
    freightrateset_keyno: string;
    freightrateset_name: string;
    orderby: string;
}

export interface FreightRatesParams {
    freightrateset_keyno: string | number;
}

export interface FreightRateItem {
    freightrate_keyno: string;
    freightratetype_name: string;
    orderby: string;
}

export interface GetDepartmentParams {
    department_id: string;
}

export interface DepartmentGet {
    department_id: string;
    department_name: string;
    businessco_no: string;
    delivery_cust_no: string;
    delivery_cust_name: string;
    delivery_address: string;
}

export interface PersonInfoParams {
    pers_no: string | number;
}

export interface PersonInfo {
    pers_telephones: string;
    pers_email: string;
}

export interface PurchHeadGetParams {
    purch_internal_no: number | `${number}`;
}

export interface PurchHeadGet {
    supplier_no: string | number;
    supplier_name: string;
    supplier_address: string;
    label: string;
    suppliers_orderno: string;
    supplier_pers_no: string | number;
    supplier_pers_telephone: string;
    supplier_pers_email: string;
    project_keyno: string | number;
    project_name: string;
    message_to_supplier: string; // RHE 20240610 Lagt til
    purch_heading: string;
    //Adm
    purch_internal_no: string | number;
    purch_request_no: string | number;
    purch_order_no: string | number;
    order_date: string;
    portal_user_keyno: string | number;
    user_id: string;
    our_ref: string;
    purchasestatus_id: string;
    businessco_no: string | number;
    businessco_companyname: string;
    stokcplace_id: string;
    change_stokcplace_id_is_possible: '1' | '0';
    stockplace_name: string;
    portal_user_keyno_initiated_by: string | number;
    show_convert_to_purchorder: '1' | '0';
    tracktrace_url: string;
    order_internal_no: string | number;
    order_order_no: string | number;
    ischanged: '1' | '0';
    consernorder_internal_no: string | number;
    consernorder_order_no: string | number;
    show_purchorder_to_salesorder: '1' | '0';
    progress_text_purchorder_to_salesorder: string;
    show_progress_text_purchorder_to_salesorder: '1' | '0';

    //Delivery
    delivery_cust_no: string | number;
    delivery_name: string;
    delivery_address: string;
    //Ureg mottaks-adresse
    delivery_cust_unreg: '1' | '0';
    delivery_cust_name: string;
    delivery_adr_street: string;
    delivery_adr_mail: string;
    delivery_postplace_id: string;
    delivery_postplace_id_street: string;
    delivery_country_id: string;
    delivery_postplace_name_street: string;
    delivery_postplace_name: string;
    department_id: string;
    show_department_id: '1' | '0';
    //Begingelser/Økonomi
    order_valuta_id: string;
    invoice_valuta_id: string;
    pricelist_id: string;
    valuta_exchangerate_now: string | number;
    exchangerate_locked: '1' | '0';
    valuta_exchangerate_locked: string | number;
    valuta_exchangefactor_locked: string | number;
    order_valuta_pr: string | number;
    valuta_label: string;
    basecurrency_id: string;

    //Betingelser/Leveranse
    deliverydate_wish: string;
    deliverydate_text: string;
    deliverydate_confirmed: string;
    deliveryterm_no: string | number;
    isconfirmed: '1' | '0';
    deliverymethod_no: string | number;
    deliverydate_exfactory_requested: string;
    deliverydate_exfactory_confirmed: string;
    deliverydate_exfactory_shipped: string;
    shipment_id: string;
    container_id: string;

    //Functions
    nbr_of_documents: string | number;
    nbr_of_batchdeliveries: string | number;
    show_purchhead_delete: '1' | '0';
    show_setting: '1' | '0';

    //Annet
    freightrateset_keyno: string | number;
    show_freightrateset: '1' | '0';
    freightrates_changed: '1' | '0';
    freightrate_keyno: string | number;
    freight_main_rate_locval: string | number;
    freight_last_rate_locval: string | number;
    customs_last_rate_locval: string | number;
    show_goto_currency: '1' | '0';
    show_button_deviations: '1' | '0';
    nbr_of_deviations: string | number;
    show_stocktransallocation: '1' | '0';
    show_purchhead_cancel_rest: '1' | '0';
    show_tracesearch: '1' | '0';
    gb_main: string;
    show_create_bachdelivery_button: '1' | '0';

    setting_show_ph_head_supplier_no: '1' | '0';
    setting_show_ph_head_supplier_name: '1' | '0';
    setting_show_ph_head_supplier_address: '1' | '0';
    setting_show_ph_head_supplier_pers_no: '1' | '0';
    setting_show_ph_head_label: '1' | '0';
    setting_show_ph_head_supplier_pers_telephone: '1' | '0';
    setting_show_ph_head_supplier_pers_email: '1' | '0';
    setting_show_ph_head_suppliers_orderno: '1' | '0';
    setting_show_ph_head_project_name: '1' | '0';
    setting_show_ph_head_message_to_supplier: '1' | '0';

    setting_show_ph_head_delivery_cust_unreg: '1' | '0';
    setting_show_ph_head_delivery_cust_no: '1' | '0';
    setting_show_ph_head_delivery_name: '1' | '0';
    setting_show_ph_head_delivery_address: '1' | '0';
    setting_show_ph_head_delivery_cust_name: '1' | '0';
    setting_show_ph_head_delivery_adr_street: '1' | '0';
    setting_show_ph_head_delivery_postplace_id_street: '1' | '0';
    setting_show_ph_head_delivery_postplace_name_street: '1' | '0';
    setting_show_ph_head_delivery_country_id: '1' | '0';
    setting_show_ph_head_delivery_adr_mail: '1' | '0';
    setting_show_ph_head_delivery_postplace_id: '1' | '0';
    setting_show_ph_head_delivery_postplace_name: '1' | '0';

    setting_show_ph_head_deliverydate_exfactory_requested: '1' | '0';
    setting_show_ph_head_shipment_id: '1' | '0';
    setting_show_ph_head_container_id: '1' | '0';
    setting_show_ph_head_deliverydate_exfactory_confirmed: '1' | '0';
    setting_show_ph_head_deliverydate_exfactory_shipped: '1' | '0';
    setting_show_ph_head_deliverydate_wish: '1' | '0';
    setting_show_ph_head_deliverydate_text: '1' | '0';
    setting_show_ph_head_deliverydate_confirmed: '1' | '0';
    setting_show_ph_head_isconfirmed: '1' | '0';
    setting_show_ph_head_deliveryterm_no: '1' | '0';
    setting_show_ph_head_deliverymethod_no: '1' | '0';

    setting_show_ph_head_order_valuta_id: '1' | '0';
    setting_show_ph_head_valuta_exchangerate_now: '1' | '0';
    setting_show_ph_head_exchangerate_locked: '1' | '0';
    setting_show_ph_head_valuta_exchangerate_locked: '1' | '0';
    setting_show_ph_head_order_valuta_pr: '1' | '0';
    setting_show_ph_head_valuta_exchangefactor_locked: '1' | '0';
    setting_show_ph_head_pricelist_id: '1' | '0';

    setting_show_ph_head_freightrateset_keyno: '1' | '0';
    setting_show_ph_head_freightrate_keyno: '1' | '0';
    setting_show_ph_head_freightrates_changed: '1' | '0';
    setting_show_ph_head_freight_main_rate_locval: '1' | '0';
    setting_show_ph_head_freight_last_rate_locval: '1' | '0';
    setting_show_ph_head_customs_last_rate_locval: '1' | '0';

    setting_show_ph_head_businessco_companyname: '1' | '0';
    setting_show_ph_head_stockplace_id: '1' | '0';
    setting_show_ph_head_purchasestatus_id: '1' | '0';
    setting_show_ph_head_portal_user_keyno: '1' | '0';
    setting_show_ph_head_portal_user_keyno_initiated_by: '1' | '0';
    setting_show_ph_head_user_id: '1' | '0';
    setting_show_ph_head_purch_order_no: '1' | '0';
    setting_show_ph_head_order_order_no: '1' | '0';
    setting_show_ph_head_order_date: '1' | '0';
    setting_show_ph_head_our_ref: '1' | '0';
    setting_show_ph_head_tracktrace_url: '1' | '0';
    setting_show_ph_head_consernorder_order_no: '1' | '0';
    // head_tab: string;
    // address_tab: string;
    // terms_tab: string;
}

export interface ConvertToSalesOrderProgressParams {
    purch_internal_no: number | `${number}`;
}

export interface ConvertToSalesOrderProgress {
    progress_text_purchorder_to_salesorder: string;
}

export interface ConvertPurchOrderToSalesOrderParams {
    purch_internal_no: number | `${number}`;
}

export interface PurchHeadsGetParams {
    argtype: string;
    argvalue: string;
    webpage: string;
}

export interface PurchHeadsGet {
    argtype: string;
    argvalue: string;
    webpage: string;
    supplier_no: number | `${number}`;
    searchtext_supplier: string;
    purchasestatus_id: string;
    filterval: string;
    supplier_name: string;
    gb_purchdate: string;
    gb_purchasetype: string;
    gb_purchasetype_purch: string;
    gb_whos: string;
    supplier_no_and_name: string;
    portal_user_keyno: number | `${number}`;
    businessco_no: number | `${number}`;
    searchtext: string;
    current_portal_user_keyno: number | `${number}`;
    show_create_bachdelivery_button: '1' | '0';
}

export interface GetPurchHeadReceiveSimplifiedParams {
    purch_internal_no: string | `${number}` | number;
}

export interface PurchHeadReceiveSimplifiedGet {
    purch_internal_no: string | number;
    freight_eu: string | number;
    freight_ex_eu: string | number;
    customs_eu: string | number;
    customs_ex_eu: string | number;
    divcost: string | number;
    stockplace_name: string;
    stockplace_id: string;
    stocklocation_keyno: string;
    show_stocklocation_keyno: '1' | '0';
    webpage_heading?: string;
}
